import { initGlobalMotion } from '../core/motion';

const ALL_TAG_KEY = '__all__';

const normalize = (value: string): string => value.trim().toLowerCase();

const getPostCards = (root: HTMLElement): HTMLElement[] =>
    Array.from(root.querySelectorAll<HTMLElement>('[data-blog-card]'));

export const initBlogIndex = (): void => {
    const root = document.getElementById('blogPage');
    if (!root) return;
    initGlobalMotion();
};

export const initBlogList = (): void => {
    const root = document.getElementById('blogListPage');
    if (!root) return;

    const list = root.querySelector<HTMLElement>('#blogList');
    const tagSelect = root.querySelector<HTMLSelectElement>('#blogTag');
    const search = root.querySelector<HTMLInputElement>('#blogSearch');
    const sort = root.querySelector<HTMLSelectElement>('#blogSort');
    const empty = root.querySelector<HTMLElement>('#blogListEmpty');

    if (!list || !tagSelect || !search || !sort) return;

    let activeTag = tagSelect.value || ALL_TAG_KEY;
    let query = '';

    const render = (): void => {
        const cards = getPostCards(list);
        const visibleCards = cards.filter(card => {
            const title = normalize(card.dataset.title ?? '');
            const cardTags = normalize(card.dataset.tags ?? '');
            const category = normalize(card.dataset.category ?? '');
            const matchesTag = activeTag === ALL_TAG_KEY || cardTags.split('|').some(tag => tag === normalize(activeTag));
            const matchesQuery = !query || title.includes(query) || cardTags.includes(query) || category.includes(query);
            return matchesTag && matchesQuery;
        });

        const sorted = [...cards].sort((a, b) => {
            switch (sort.value) {
                case 'published-asc':
                    return Date.parse(a.dataset.published ?? '') - Date.parse(b.dataset.published ?? '');
                case 'title-asc':
                    return (a.dataset.title ?? '').localeCompare(b.dataset.title ?? '');
                case 'most-read':
                    return Number(a.dataset.rank ?? '999') - Number(b.dataset.rank ?? '999');
                default:
                    return Date.parse(b.dataset.published ?? '') - Date.parse(a.dataset.published ?? '');
            }
        });

        sorted.forEach(card => list.appendChild(card));
        cards.forEach(card => {
            card.hidden = !visibleCards.includes(card);
        });

        if (empty) {
            empty.hidden = visibleCards.length > 0;
        }
    };

    tagSelect.addEventListener('change', () => {
        activeTag = tagSelect.value || ALL_TAG_KEY;
        window.japonicsAnalytics?.track('filter_apply', {
            filter_group: 'blog_tag',
            filter_value: activeTag
        });
        render();
    });

    search.addEventListener('input', () => {
        query = normalize(search.value);
        render();
    });

    sort.addEventListener('change', () => {
        window.japonicsAnalytics?.track('filter_apply', {
            filter_group: 'blog_sort',
            filter_value: sort.value
        });
        render();
    });
    render();
    initGlobalMotion();
};

export const initBlogPost = (): void => {
    const root = document.getElementById('blogPostPage');
    if (!root) return;

    initReadingProgress();
    initToc(root);
    initCodeCopy(root);
    initGlobalMotion();
};

const initReadingProgress = (): void => {
    const progress = document.getElementById('blogProgress');
    if (!progress) return;

    const update = (): void => {
        const total = document.documentElement.scrollHeight - window.innerHeight;
        const ratio = total > 0 ? Math.min(1, Math.max(0, window.scrollY / total)) : 0;
        progress.style.transform = `scaleX(${ratio})`;
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
};

const initToc = (root: HTMLElement): void => {
    const links = Array.from(root.querySelectorAll<HTMLAnchorElement>('#blogToc a[href^="#"]'));
    if (!links.length) return;

    const headings = links
        .map(link => document.getElementById(link.getAttribute('href')?.slice(1) ?? ''))
        .filter((heading): heading is HTMLElement => Boolean(heading));

    if (!headings.length) return;

    const setActiveLink = (id: string): void => {
        links.forEach(link => {
            link.classList.toggle('is-active', link.getAttribute('href') === `#${id}`);
        });
    };

    links.forEach(link => {
        link.addEventListener('click', event => {
            const id = link.getAttribute('href')?.slice(1);
            const heading = id ? document.getElementById(id) : null;
            if (!id || !heading) return;

            event.preventDefault();
            const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            heading.scrollIntoView({
                behavior: reduceMotion ? 'auto' : 'smooth',
                block: 'start'
            });
            window.history.pushState(null, '', `#${id}`);
            setActiveLink(id);
        });
    });

    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            const id = entry.target.getAttribute('id');
            if (id) setActiveLink(id);
        });
    }, { rootMargin: '0px 0px -70% 0px' });

    headings.forEach(heading => observer.observe(heading));
};

const initCodeCopy = (root: HTMLElement): void => {
    root.querySelectorAll<HTMLPreElement>('.blogpost-content pre').forEach(block => {
        if (block.classList.contains('code-block')) return;

        const code = block.querySelector('code');
        if (!code) return;

        block.classList.add('code-block');

        const header = document.createElement('div');
        header.className = 'code-block__header';

        const label = document.createElement('span');
        label.className = 'code-block__label';
        const language = Array.from(code.classList).find(name => name.startsWith('language-'));
        label.textContent = language ? language.replace('language-', '') : 'code';
        header.appendChild(label);

        const copy = document.createElement('button');
        copy.type = 'button';
        copy.className = 'code-block__copy';
        copy.textContent = 'Copy';
        copy.addEventListener('click', async () => {
            try {
                await navigator.clipboard.writeText(code.textContent ?? '');
                copy.textContent = 'Copied';
                window.setTimeout(() => {
                    copy.textContent = 'Copy';
                }, 1400);
            } catch {
                copy.textContent = 'Error';
            }
        });
        header.appendChild(copy);

        block.insertBefore(header, block.firstChild);
    });
};
